import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useStore from "../../store/useStore";
import WishlistButton from "./WishlistButton";

const StarRating = ({ rating = 4.5, count = 128 }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg
          key={star}
          className={`h-4 w-4 ${star <= Math.round(rating) ? "text-amber-400" : "text-gray-200"}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
    <span className="text-sm font-semibold text-gray-700">{rating}</span>
    <span className="text-sm text-gray-400">({count} reviews)</span>
  </div>
);

const Badge = ({ children, color = "green" }) => {
  const colors = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${colors[color]}`}
    >
      {children}
    </span>
  );
};

const isSameCartVariant = (item, productId, variant) => {
  if (String(item.product_id) !== String(productId)) return false;
  if (!variant) return !item.product_variant_id && !item.variant_id;

  if (variant.product_variant_id) {
    return (
      String(item.product_variant_id) === String(variant.product_variant_id)
    );
  }

  if (variant.variant_id) {
    return String(item.variant_id) === String(variant.variant_id);
  }

  return false;
};

const ProductDetailView = ({
  product,
  addToCart,
  cart,
  cartLoading,
  isAuthenticated,
  openAuthModal,
}) => {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(
    product.variants?.[0] || null,
  );
  const [quantity, setQuantity] = useState(1);
  const [showMore, setShowMore] = useState(false);
  const [imgError, setImgError] = useState({});
  const autoScrollRef = useRef(null);

  const images = product.images || [];

  const startAutoScroll = (imgCount) => {
    clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setSelectedImage((prev) => (prev + 1) % imgCount);
    }, 5000);
  };

  useEffect(() => {
    if (images.length <= 1) return;
    startAutoScroll(images.length);
    return () => clearInterval(autoScrollRef.current);
  }, [images.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (images.length <= 1) return;
    const diff = touchStartX.current - touchEndX.current;
    if (diff > 50) {
      // Swipe left -> next image
      setSelectedImage((prev) => (prev + 1) % images.length);
      startAutoScroll(images.length);
    } else if (diff < -50) {
      // Swipe right -> previous image
      setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
      startAutoScroll(images.length);
    }
  };

  const handleImageClick = () => {
    if (images.length <= 1) return;
    // Disable click-to-scroll on mobile/tablet screens (must swipe instead)
    if (window.innerWidth < 1024) return;

    setSelectedImage((prev) => (prev + 1) % images.length);
    // Reset the auto-scroll timer so it doesn't fire immediately after manual click
    startAutoScroll(images.length);
  };
  const isInCart = cart.some((item) =>
    isSameCartVariant(item, product.product_id, selectedVariant),
  );
  const currentPrice = selectedVariant
    ? parseFloat(selectedVariant.discount_price)
    : null;
  const currentOriginalPrice = selectedVariant
    ? parseFloat(selectedVariant.original_price)
    : null;
  const discount =
    currentOriginalPrice && currentPrice && currentOriginalPrice > currentPrice
      ? Math.round(
          ((currentOriginalPrice - currentPrice) / currentOriginalPrice) * 100,
        )
      : 0;
  const savings =
    currentOriginalPrice && currentPrice
      ? (currentOriginalPrice - currentPrice).toFixed(2)
      : 0;

  const handleAddToCart = async () => {
    if (isInCart) {
      navigate("/cart");
      return;
    }
    if (!selectedVariant) {
      toast.error("Please select a size/weight");
      return;
    }
    await addToCart({ ...product, selectedVariant }, quantity);
    // Use a stable toast ID so repeated quick clicks never stack multiple toasts
    toast.success("Added to cart!", {
      id: `add-cart-${product.product_id ?? product.id}`,
    });
  };

  const handleBuyNow = async () => {
    if (!selectedVariant) {
      toast.error("Please select a size/weight");
      return;
    }
    if (!isAuthenticated) {
      toast("Please login to place your order", { icon: "🔒" });
      if (openAuthModal) openAuthModal();
      return;
    }
    // Must be in cart before proceeding to checkout
    if (!isInCart) {
      toast("Please add the item to cart first", { icon: "🛒" });
      await addToCart({ ...product, selectedVariant }, quantity);
      toast.success("Added to cart! You can now proceed.", {
        id: `add-cart-${product.product_id ?? product.id}`,
      });
      return;
    }
    navigate('/cart');
  };

  const handleSubscribe = () => {
    if (!selectedVariant) {
      toast.error("Please select a size/weight");
      return;
    }
    if (!isAuthenticated) {
      toast("Please login to start a subscription");
      if (openAuthModal) openAuthModal();
      return;
    }

    const params = new URLSearchParams({
      productId: String(product.product_id),
      variantId: String(selectedVariant.product_variant_id),
    });
    navigate(`/account/subscriptions?${params.toString()}`);
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          <div className="relative overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-lg">
            {discount > 0 && (
              <div className="absolute left-4 top-4 z-10 rounded-full bg-red-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg">
                {discount}% OFF
              </div>
            )}
            <div
              className={`flex aspect-square items-center justify-center p-8 ${images.length > 1 ? "lg:cursor-pointer select-none" : ""}`}
              onClick={handleImageClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              title={
                images.length > 1
                  ? window.innerWidth >= 1024
                    ? "Click to see next image"
                    : undefined
                  : undefined
              }
            >
              {images[selectedImage] && !imgError[selectedImage] ? (
                <img
                  src={images[selectedImage]}
                  alt={`${product.name} - view ${selectedImage + 1}`}
                  className="h-full w-full object-contain transition-all duration-500 pointer-events-none"
                  onError={() =>
                    setImgError((prev) => ({ ...prev, [selectedImage]: true }))
                  }
                />
              ) : (
                <div className="flex flex-col items-center gap-3 text-gray-300">
                  <svg
                    className="h-24 w-24"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2 1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z"
                    />
                  </svg>
                  <span className="text-sm font-medium">No Image</span>
                </div>
              )}
            </div>

            {images.length > 1 && (
              <>
                <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/90 px-3 py-2 shadow-md backdrop-blur">
                  {images.map((_, index) => (
                    <span
                      key={index}
                      className={`h-1.5 rounded-full transition-all ${selectedImage === index ? "w-5 bg-primary-600" : "w-1.5 bg-gray-300"}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {product.category_name && (
              <Badge color="green">{product.category_name}</Badge>
            )}
            {product.subcategory_name && (
              <Badge color="blue">{product.subcategory_name}</Badge>
            )}
            {product.brand_name && (
              <Badge color="orange">{product.brand_name}</Badge>
            )}
          </div>

          <div>
            <h1 className="w-full text-xl font-black tracking-tight text-gray-900 sm:text-4xl">
              {product.name}
            </h1>
            {(product.product_name_hindi || product.product_name_marathi) && (
              <p className="mt-1 text-sm text-gray-500">
                {[product.product_name_hindi, product.product_name_marathi]
                  .filter(Boolean)
                  .join(" / ")}
              </p>
            )}
          </div>

          <StarRating />

          {selectedVariant ? (
            <div className="rounded-2xl border border-primary-100 bg-primary-50 p-5">
              <div className="flex flex-wrap items-start gap-3">
                <div className="relative inline-flex items-start">
                  <span className="text-4xl font-black text-primary-600">
                    Rs {currentPrice?.toFixed(2)}
                  </span>
                  {currentOriginalPrice > currentPrice && (
                    <span className="ml-1 text-xs font-semibold text-gray-400 line-through leading-none">
                      Rs {currentOriginalPrice?.toFixed(2)}
                    </span>
                  )}
                </div>
                {discount > 0 && (
                  <span className="rounded-full bg-red-500 px-2.5 py-1 text-xs font-bold text-white self-center">
                    {discount}% OFF
                  </span>
                )}
              </div>
              {savings > 0 && (
                <p className="mt-1 text-sm font-semibold text-emerald-600">
                  You save Rs {savings} on this order.
                </p>
              )}
              <div className="mt-3 flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                <span className="text-sm font-semibold text-emerald-600">
                  In Stock - Ready to ship
                </span>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
              <p className="text-sm text-gray-500">
                Select a size/weight to see the price
              </p>
            </div>
          )}

          {product.variants?.length > 0 && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900 sm:text-base">
                  Select Size / Weight
                </h3>
                {selectedVariant && (
                  <span className="text-xs font-semibold text-primary-600 sm:text-sm">
                    {selectedVariant.variant_name}
                    {selectedVariant.unit_name
                      ? ` ${selectedVariant.unit_name}`
                      : ""}{" "}
                    selected
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                {product.variants.map((variant) => {
                  const isSelected =
                    selectedVariant?.product_variant_id ===
                    variant.product_variant_id;
                  const hasDiscount =
                    parseFloat(variant.original_price) >
                    parseFloat(variant.discount_price);

                  return (
                    <button
                      key={variant.product_variant_id}
                      type="button"
                      onClick={() => setSelectedVariant(variant)}
                      className={`relative flex min-w-[90px] flex-col items-center gap-1 rounded-2xl border-2 px-5 py-3 text-center transition-all duration-200 ${isSelected ? "scale-105 border-primary-500 bg-primary-50 shadow-md shadow-primary-100" : "border-gray-200 bg-white hover:border-primary-300 hover:bg-primary-50/30"}`}
                    >
                      {hasDiscount && (
                        <span className="absolute -right-2 -top-2 rounded-full bg-red-500 px-1.5 py-0.5 text-[9px] font-bold text-white">
                          {Math.round(
                            ((parseFloat(variant.original_price) -
                              parseFloat(variant.discount_price)) /
                              parseFloat(variant.original_price)) *
                              100,
                          )}
                          %
                        </span>
                      )}
                      <span className="text-sm font-bold leading-tight text-gray-900">
                        {variant.variant_name}
                        {variant.unit_name ? ` ${variant.unit_name}` : ""}
                      </span>
                      <span className="text-sm font-extrabold text-primary-600">
                        Rs {parseFloat(variant.discount_price).toFixed(0)}
                      </span>
                      {hasDiscount && (
                        <span className="text-[10px] text-gray-400 line-through">
                          Rs {parseFloat(variant.original_price).toFixed(0)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {/* Actions row - wishlist and quantity on top for mobile */}
            <div className="flex items-center justify-between gap-3 sm:hidden">
              <div className="w-fit">
                <h3 className="mb-2 text-xs font-bold text-gray-600">Qty</h3>
                <div className="flex items-center overflow-hidden rounded-xl border border-gray-300 bg-white">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="flex h-9 w-9 items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-50"
                  >
                    -
                  </button>
                  <span className="flex h-9 w-10 items-center justify-center text-sm font-bold text-gray-900">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="flex h-9 w-9 items-center justify-center text-lg font-bold text-gray-600 hover:bg-gray-50"
                  >
                    +
                  </button>
                </div>
              </div>
              <div className="pt-6">
                <WishlistButton productId={product.product_id} size="md" />
              </div>
            </div>

            {/* Desktop quantity display */}
            <div className="hidden sm:block">
              <h3 className="mb-3 font-bold text-gray-900">Quantity</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center overflow-hidden rounded-2xl border-2 border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="flex h-12 w-12 items-center justify-center text-xl font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary-600"
                  >
                    -
                  </button>
                  <span className="flex h-12 w-14 items-center justify-center border-x-2 border-gray-200 text-lg font-bold text-gray-900">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="flex h-12 w-12 items-center justify-center text-xl font-bold text-gray-600 transition-colors hover:bg-gray-50 hover:text-primary-600"
                  >
                    +
                  </button>
                </div>
                {selectedVariant && (
                  <span className="text-sm text-gray-500">
                    Total:{" "}
                    <span className="font-bold text-gray-900">
                      Rs {(currentPrice * quantity).toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={(!selectedVariant && !isInCart) || cartLoading}
                className={`flex flex-1 items-center justify-center gap-2 rounded-2xl border-2 py-4 text-base font-bold shadow-sm transition-all hover:shadow-md active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 ${isInCart ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700" : "border-primary-500 bg-white text-primary-600 hover:bg-primary-50"}`}
              >
                {cartLoading
                  ? "Adding..."
                  : isInCart
                    ? "View Cart"
                    : "Add to Cart"}
              </button>
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={!selectedVariant || cartLoading}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-primary-600 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isInCart ? "Go to Cart" : "Buy Now"}
              </button>
              <button
                type="button"
                onClick={handleSubscribe}
                disabled={!selectedVariant || cartLoading}
                className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-700 py-4 text-base font-bold text-white shadow-lg transition-all hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Subscribe & Save
              </button>
              <div className="hidden sm:block">
                <WishlistButton productId={product.product_id} size="lg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-12 overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm">
        {showMore && (
          <div className="p-6 sm:p-8 space-y-8">
            {product.description && (
              <div>
                <h3 className="mb-3 text-base font-bold text-gray-900">
                  Description
                </h3>
                <div
                  className="prose prose-sm max-w-none text-gray-700 prose-headings:text-gray-900 prose-strong:text-gray-900 sm:prose"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>
            )}
            {product.specifications &&
              product.specifications.replace(/<[^>]*>/g, "").trim() && (
                <div>
                  <h3 className="mb-3 text-base font-bold text-gray-900">
                    Specifications
                  </h3>
                  <div
                    className="prose prose-sm max-w-none text-gray-700 prose-headings:text-gray-900 prose-strong:text-gray-900 sm:prose"
                    dangerouslySetInnerHTML={{ __html: product.specifications }}
                  />
                </div>
              )}
            <div>
              <h3 className="mb-3 text-base font-bold text-gray-900">
                Product Details
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {[
                  { label: "Category", value: product.category_name },
                  { label: "Subcategory", value: product.subcategory_name },
                  { label: "Brand", value: product.brand_name },
                  {
                    label: "Available Sizes",
                    value: product.variants
                      ?.map((v) => `${v.variant_name}${v.unit_name ? ` ${v.unit_name}` : ''}`)
                      .join(", "),
                  },
                  {
                    label: "Price Range",
                    value:
                      product.variants?.length > 0
                        ? `Rs ${Math.min(...product.variants.map((v) => v.discount_price)).toFixed(0)} - Rs ${Math.max(...product.variants.map((v) => v.discount_price)).toFixed(0)}`
                        : null,
                  },
                  { label: "In Stock", value: "Yes" },
                  { label: "Diet Type", value: "Vegetarian" },
                  { label: "Country of Origin", value: "India" },
                ]
                  .filter((row) => row.value)
                  .map(({ label, value }) => (
                    <div
                      key={label}
                      className="flex items-start justify-between gap-4 rounded-xl bg-gray-50 px-4 py-3"
                    >
                      <span className="shrink-0 text-sm font-semibold text-gray-500">
                        {label}
                      </span>
                      <span className="text-right text-sm font-bold text-gray-900">
                        {value}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
        <div
          className={`flex justify-center px-6 py-4 ${showMore ? "border-t border-gray-100" : ""}`}
        >
          <button
            type="button"
            onClick={() => setShowMore((prev) => !prev)}
            className="flex items-center gap-2 rounded-full border border-primary-500 px-6 py-2.5 text-sm font-bold text-primary-600 transition-all hover:bg-primary-50"
          >
            {showMore ? "Show Less" : "Show More"}
            <svg
              className={`h-4 w-4 transition-transform duration-200 ${showMore ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default ProductDetailView;
